import { useState, useEffect, useCallback } from "react";
import { t, font, Button, Input, Badge } from "@realtyflows/ui";
import { getDealOffers, createDealOffer, updateDealOffer, deleteDealOffer } from "../api";

/**
 * OfferBoard — V2 Feature 2.11
 *
 * Per-deal offer comparison + management. Renders the offer list with
 * side-by-side key terms (price, earnest money, financing, close date,
 * contingencies) and lets the agent accept / counter / reject.
 *
 * Accepting an offer auto-supersedes the others on the server.
 */

const STATUS_META = {
  received:   { label: "Received",   color: "sky" },
  countered:  { label: "Countered",  color: "amber" },
  accepted:   { label: "Accepted",   color: "emerald" },
  rejected:   { label: "Rejected",   color: "coral" },
  withdrawn:  { label: "Withdrawn",  color: "muted" },
  superseded: { label: "Superseded", color: "muted" },
};
const colorOf = (n) => ({ emerald: t.emerald, amber: t.amber, sky: t.sky, coral: t.coral, muted: t.textMuted })[n] || t.textMuted;

const FINANCING_OPTIONS = [
  { id: "", label: "—" },
  { id: "cash", label: "Cash" },
  { id: "conventional", label: "Conventional" },
  { id: "fha", label: "FHA" },
  { id: "va", label: "VA" },
  { id: "other", label: "Other" },
];

const fmtMoney = (cents) => {
  if (cents == null) return "—";
  return `$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
};

export default function OfferBoard({ dealId }) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [draft, setDraft] = useState(blankOffer());

  function blankOffer() {
    return {
      buyer_name: "", buyer_email: "", buyer_agent_name: "", buyer_agent_email: "",
      offer_price: "", earnest_money: "", financing: "", contingencies: "",
      close_date: "", notes: "",
    };
  }

  const refresh = useCallback(() => {
    if (!dealId) return;
    setLoading(true);
    getDealOffers(dealId)
      .then(setOffers)
      .catch(() => setOffers([]))
      .finally(() => setLoading(false));
  }, [dealId]);
  useEffect(refresh, [refresh]);

  const handleAdd = async () => {
    if (!draft.buyer_name?.trim() && !draft.offer_price) {
      window.alert("Enter at least a buyer name or offer price");
      return;
    }
    setBusyId("__new__");
    try {
      await createDealOffer(dealId, {
        ...draft,
        offer_price: draft.offer_price ? Math.round(parseFloat(draft.offer_price) * 100) : null,
        earnest_money: draft.earnest_money ? Math.round(parseFloat(draft.earnest_money) * 100) : null,
        contingencies: draft.contingencies ? draft.contingencies.split(",").map((s) => s.trim()).filter(Boolean) : [],
      });
      setDraft(blankOffer());
      setShowAdd(false);
      refresh();
    } catch (err) {
      window.alert(err.message || "Could not record offer");
    } finally {
      setBusyId(null);
    }
  };

  const handleStatus = async (offer, status) => {
    if (status === "accepted" && offer.status !== "accepted") {
      if (!window.confirm(`Accept this offer? All other received offers will be marked superseded.`)) return;
    }
    let counter_offer_terms = null;
    if (status === "countered") {
      counter_offer_terms = window.prompt("Describe your counter-offer terms:", offer.counter_offer_terms || "");
      if (counter_offer_terms === null) return;
    }
    setBusyId(offer.id);
    try {
      await updateDealOffer(dealId, offer.id, { status, counter_offer_terms });
      refresh();
    } catch (err) {
      window.alert(err.message || "Could not update offer");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (offer) => {
    if (!window.confirm("Permanently delete this offer? This cannot be undone.")) return;
    try { await deleteDealOffer(dealId, offer.id); refresh(); }
    catch (err) { window.alert(err.message || "Could not delete"); }
  };

  if (loading) {
    return <div style={{ fontSize: 12, color: t.textMuted, padding: 10, fontFamily: font.body }}>Loading offers...</div>;
  }

  const active = offers.filter((o) => o.status === "received" || o.status === "countered");
  const resolved = offers.filter((o) => o.status !== "received" && o.status !== "countered");

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div>
          <span style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
            {offers.length} offer{offers.length === 1 ? "" : "s"}
          </span>
          <span style={{ fontSize: 11, color: t.textMuted, marginLeft: 8, fontFamily: font.body }}>
            {active.length} active, {resolved.length} resolved
          </span>
        </div>
        <Button variant="primary" size="sm" onClick={() => setShowAdd((v) => !v)}>
          {showAdd ? "Cancel" : "+ Record offer"}
        </Button>
      </div>

      {showAdd && (
        <div style={{ padding: 14, background: t.surfaceWarm, border: `1px solid ${t.border}`, borderRadius: 10, marginBottom: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Input placeholder="Buyer name" value={draft.buyer_name} onChange={(e) => setDraft({ ...draft, buyer_name: e.target.value })} />
            <Input placeholder="Buyer email" value={draft.buyer_email} onChange={(e) => setDraft({ ...draft, buyer_email: e.target.value })} />
            <Input placeholder="Buyer's agent name" value={draft.buyer_agent_name} onChange={(e) => setDraft({ ...draft, buyer_agent_name: e.target.value })} />
            <Input placeholder="Buyer's agent email" value={draft.buyer_agent_email} onChange={(e) => setDraft({ ...draft, buyer_agent_email: e.target.value })} />
            <Input type="number" placeholder="Offer price ($)" value={draft.offer_price} onChange={(e) => setDraft({ ...draft, offer_price: e.target.value })} />
            <Input type="number" placeholder="Earnest money ($)" value={draft.earnest_money} onChange={(e) => setDraft({ ...draft, earnest_money: e.target.value })} />
            <select
              value={draft.financing}
              onChange={(e) => setDraft({ ...draft, financing: e.target.value })}
              style={{ padding: "8px 12px", fontSize: 13, border: `1.5px solid ${t.border}`, borderRadius: 6, background: t.surfaceWarm, color: t.textPrimary, fontFamily: font.body, outline: "none" }}
            >
              {FINANCING_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            <Input type="date" placeholder="Close date" value={draft.close_date} onChange={(e) => setDraft({ ...draft, close_date: e.target.value })} />
            <Input placeholder="Contingencies (comma-separated)" value={draft.contingencies} onChange={(e) => setDraft({ ...draft, contingencies: e.target.value })} style={{ gridColumn: "1 / -1" }} />
            <Input placeholder="Notes" value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} style={{ gridColumn: "1 / -1" }} />
          </div>
          <div style={{ marginTop: 10 }}>
            <Button variant="primary" size="sm" onClick={handleAdd} disabled={busyId === "__new__"}>
              {busyId === "__new__" ? "Saving..." : "Save offer"}
            </Button>
          </div>
        </div>
      )}

      {/* Comparison table — active offers */}
      {active.length > 0 && (
        <div style={{ overflowX: "auto", marginBottom: 14 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: font.body }}>
            <thead>
              <tr style={{ background: t.surfaceWarm, color: t.textMuted, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
                {["Buyer", "Price", "Earnest", "Financing", "Close", "Contingencies", "Status", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "8px 10px", textAlign: "left", borderBottom: `1px solid ${t.border}`, fontFamily: font.mono, fontWeight: 700 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {active.map((o) => (
                <tr key={o.id} style={{ borderBottom: `1px solid ${t.border}` }}>
                  <td style={{ padding: "10px", color: t.textPrimary }}>
                    <div style={{ fontWeight: 600 }}>{o.buyer_name || "—"}</div>
                    {o.buyer_agent_name && (
                      <div style={{ fontSize: 10, color: t.textMuted }}>via {o.buyer_agent_name}</div>
                    )}
                  </td>
                  <td style={{ padding: "10px", fontFamily: font.mono, fontWeight: 700 }}>{fmtMoney(o.offer_price)}</td>
                  <td style={{ padding: "10px", fontFamily: font.mono }}>{fmtMoney(o.earnest_money)}</td>
                  <td style={{ padding: "10px" }}>{o.financing || "—"}</td>
                  <td style={{ padding: "10px", fontFamily: font.mono, fontSize: 11 }}>{o.close_date || "—"}</td>
                  <td style={{ padding: "10px", fontSize: 11 }}>
                    {(o.contingencies || []).length > 0
                      ? (o.contingencies || []).join(", ")
                      : <span style={{ color: t.textMuted }}>—</span>}
                  </td>
                  <td style={{ padding: "10px" }}>
                    <Badge color={colorOf(STATUS_META[o.status]?.color || "muted")}>
                      {STATUS_META[o.status]?.label || o.status}
                    </Badge>
                  </td>
                  <td style={{ padding: "10px" }}>
                    <div style={{ display: "flex", gap: 4 }}>
                      <Button variant="primary" size="sm" disabled={busyId === o.id} onClick={() => handleStatus(o, "accepted")}>Accept</Button>
                      <Button variant="outline" size="sm" disabled={busyId === o.id} onClick={() => handleStatus(o, "countered")}>Counter</Button>
                      <Button variant="outline" size="sm" disabled={busyId === o.id} onClick={() => handleStatus(o, "rejected")}>Reject</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Resolved offers (collapsed list) */}
      {resolved.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 10, color: t.textMuted, marginBottom: 6, fontFamily: font.mono, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>
            Resolved
          </div>
          {resolved.map((o) => (
            <div key={o.id} style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "6px 10px", background: t.surfaceWarm, borderRadius: 6, marginBottom: 4,
              fontSize: 12, fontFamily: font.body, opacity: 0.7,
            }}>
              <span style={{ flex: 1 }}>{o.buyer_name || "—"} · {fmtMoney(o.offer_price)}</span>
              <Badge color={colorOf(STATUS_META[o.status]?.color || "muted")}>
                {STATUS_META[o.status]?.label || o.status}
              </Badge>
              <button onClick={() => handleDelete(o)} style={{ background: "transparent", border: "none", cursor: "pointer", color: t.coral, fontSize: 14, fontWeight: 700 }}>×</button>
            </div>
          ))}
        </div>
      )}

      {offers.length === 0 && !showAdd && (
        <div style={{ fontSize: 12, color: t.textMuted, fontStyle: "italic", padding: 14, fontFamily: font.body, textAlign: "center" }}>
          No offers yet. Click "Record offer" when buyers send you their offers.
        </div>
      )}
    </div>
  );
}
